import BLSService from '@/services/bls.service';
import {
  loadExtrasMap,
  pickExtrasFromConfig,
  saveExtrasMap,
} from '@/services/bls/constants.js';
import { entityToWizardConfig, unwrapList, wizardConfigToFormDataAsync } from '@/services/bls/payload.js';
import { pickLatestSurvey } from '@/services/bls/display.js';

function notify(commit, payload) {
  commit('notifications/ADD_NOTIFICATION', payload, { root: true });
}

function persistExtras(state) {
  saveExtrasMap(state.extrasById);
}

function assertSingleSurveyCreate(state, config, campaignId) {
  if (config?.id) return;
  const id = campaignId || config?.campaignId;
  const existing = (state.byCampaignId[id] || []).filter(Boolean);
  if (existing.length) {
    throw new Error('This campaign already has a Brand Lift Survey. Edit the existing survey instead of creating a new one.');
  }
}

async function formDataForSurvey(config, sets) {
  let existingHtml;
  if (config.surveySource === 'upload' && !config.htmlFile && config.id) {
    try {
      existingHtml = await BLSService.getSurveyHtml(config.id);
    } catch (error) {
      existingHtml = undefined;
    }
  }
  return wizardConfigToFormDataAsync(config, sets, { existingHtml });
}

export const bls = {
  namespaced: true,
  state: () => ({
    byCampaignId: {},
    extrasById: loadExtrasMap(),
    reportsById: {},
    dailyById: {},
    optimizationById: {},
    loading: false,
    saving: false,
    launching: false,
  }),
  getters: {
    listForCampaign: (state) => (campaignId) => state.byCampaignId[campaignId] || [],
    extrasFor: (state) => (blsId) => (blsId && state.extrasById[blsId]) || {},
    currentForCampaign: (state) => (campaignId) => pickLatestSurvey(state.byCampaignId[campaignId] || []),
    mergedConfig: (state, getters) => (entity, campaign) => {
      if (!entity) return null;
      return entityToWizardConfig(entity, getters.extrasFor(entity.id), campaign);
    },
  },
  mutations: {
    setLoading(state, value) {
      state.loading = value;
    },
    setSaving(state, value) {
      state.saving = value;
    },
    setLaunching(state, value) {
      state.launching = value;
    },
    setList(state, { campaignId, list }) {
      state.byCampaignId = { ...state.byCampaignId, [campaignId]: list };
    },
    upsertSurvey(state, survey) {
      if (!survey?.id) return;
      const campaignId = survey.campaignId;
      if (!campaignId) return;
      const current = state.byCampaignId[campaignId] || [];
      const index = current.findIndex((item) => item.id === survey.id);
      const next = [...current];
      if (index >= 0) next.splice(index, 1, { ...next[index], ...survey });
      else next.unshift(survey);
      state.byCampaignId = { ...state.byCampaignId, [campaignId]: next };
    },
    removeSurvey(state, { campaignId, id }) {
      const current = state.byCampaignId[campaignId] || [];
      state.byCampaignId = {
        ...state.byCampaignId,
        [campaignId]: current.filter((item) => item.id !== id),
      };
    },
    setExtras(state, { id, extras }) {
      if (!id) return;
      state.extrasById = { ...state.extrasById, [id]: { ...(state.extrasById[id] || {}), ...extras } };
      persistExtras(state);
    },
    setReport(state, { id, report }) {
      state.reportsById = { ...state.reportsById, [id]: report };
    },
    setDaily(state, { id, report }) {
      state.dailyById = { ...state.dailyById, [id]: report };
    },
    setOptimization(state, { id, log }) {
      state.optimizationById = { ...state.optimizationById, [id]: log };
    },
  },
  actions: {
    cacheExtras({ commit }, config) {
      if (!config?.id) return;
      commit('setExtras', { id: config.id, extras: pickExtrasFromConfig(config) });
    },

    async list({ commit }, campaignId) {
      commit('setLoading', true);
      try {
        const list = await BLSService.listByCampaign(campaignId);
        commit('setList', { campaignId, list });
        return list;
      } catch (error) {
        notify(commit, {
          title: 'Could not load Brand Lift Surveys',
          text: error.message,
          type: 'danger',
          timeout: true,
          delay: 6000,
        });
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },

    async get({ commit }, id) {
      const entity = await BLSService.get(id);
      commit('upsertSurvey', entity);
      return entity;
    },

    async saveDraft({ commit, dispatch, state }, { config, sets, campaignId }) {
      commit('setSaving', true);
      try {
        assertSingleSurveyCreate(state, config, campaignId);
        const formData = await formDataForSurvey(config, sets);
        const entity = config.id
          ? await BLSService.update(config.id, formData)
          : await BLSService.create(campaignId || config.campaignId, formData);
        commit('upsertSurvey', entity);
        const nextConfig = { ...config, id: entity.id, status: entity.status || config.status };
        dispatch('cacheExtras', nextConfig);
        notify(commit, {
          title: 'Draft saved',
          text: 'Brand Lift Survey draft has been saved.',
          type: 'success',
          timeout: true,
          delay: 4000,
        });
        return entity;
      } catch (error) {
        notify(commit, {
          title: 'Could not save draft',
          text: error.message,
          type: 'danger',
          timeout: true,
          delay: 6000,
        });
        throw error;
      } finally {
        commit('setSaving', false);
      }
    },

    async launch({ commit, dispatch, state }, { config, sets, campaignId }) {
      commit('setLaunching', true);
      try {
        assertSingleSurveyCreate(state, config, campaignId);
        const formData = await formDataForSurvey(config, sets);
        let entity = config.id
          ? await BLSService.update(config.id, formData)
          : await BLSService.create(campaignId || config.campaignId, formData);
        if (!entity?.id) throw new Error('Keeper did not return a Brand Lift Survey id.');
        // Standard surveys often have no creative_groups. begin() currently
        // assumes groups exist, so a failure here must not block start().
        try {
          await BLSService.begin(entity.id);
        } catch (beginError) {
          console.warn('BLS begin skipped or failed', beginError);
        }
        entity = (await BLSService.start(entity.id)) || entity;
        entity = { ...entity, status: entity.status || 'active' };
        commit('upsertSurvey', entity);
        dispatch('cacheExtras', { ...config, id: entity.id, status: entity.status });
        return entity;
      } catch (error) {
        notify(commit, {
          title: 'Could not launch Brand Lift Survey',
          text: error.message,
          type: 'danger',
          timeout: true,
          delay: 7000,
        });
        throw error;
      } finally {
        commit('setLaunching', false);
      }
    },

    async pause({ commit, dispatch }, { survey, config }) {
      try {
        const entity = await BLSService.pause(survey.id);
        commit('upsertSurvey', { ...survey, ...entity, status: 'paused' });
        if (config) {
          dispatch('cacheExtras', {
            ...config,
            id: survey.id,
            status: 'paused',
            pausedAt: Math.floor(Date.now() / 1000),
          });
        }
        return entity;
      } catch (error) {
        notify(commit, {
          title: 'Could not pause Brand Lift Survey',
          text: error.message,
          type: 'danger',
          timeout: true,
          delay: 6000,
        });
        throw error;
      }
    },

    async resume({ commit, dispatch }, { survey, config }) {
      try {
        const entity = await BLSService.start(survey.id);
        commit('upsertSurvey', { ...survey, ...entity, status: 'active' });
        if (config) dispatch('cacheExtras', { ...config, id: survey.id, status: 'active', pausedAt: null });
        notify(commit, {
          title: 'Brand Lift Survey resumed',
          text: 'New eligible users will be assigned to Brand Lift paths again.',
          type: 'success',
          timeout: true,
          delay: 4000,
        });
        return entity;
      } catch (error) {
        notify(commit, {
          title: 'Could not resume Brand Lift Survey',
          text: error.message,
          type: 'danger',
          timeout: true,
          delay: 6000,
        });
        throw error;
      }
    },

    async end({ commit, dispatch }, { survey, config, campaignId }) {
      try {
        const formData = new FormData();
        formData.append('status', 'completed');
        let entity;
        try {
          entity = await BLSService.update(survey.id, formData);
        } catch (e) {
          await BLSService.remove(survey.id);
          entity = { ...survey, status: 'completed' };
        }
        commit('upsertSurvey', { ...survey, ...entity, status: 'completed' });
        if (config) dispatch('cacheExtras', { ...config, id: survey.id, status: 'completed' });
        if (campaignId) await dispatch('list', campaignId);
        return entity;
      } catch (error) {
        notify(commit, {
          title: 'Could not end Brand Lift Survey',
          text: error.message,
          type: 'danger',
          timeout: true,
          delay: 6000,
        });
        throw error;
      }
    },

    async remove({ commit }, { survey, campaignId }) {
      try {
        await BLSService.remove(survey.id);
        const id = campaignId || survey.campaignId;
        commit('removeSurvey', { campaignId: id, id: survey.id });
        notify(commit, {
          title: 'Brand Lift Survey deleted',
          text: 'The survey has been removed from this campaign.',
          type: 'success',
          timeout: true,
          delay: 4000,
        });
      } catch (error) {
        notify(commit, {
          title: 'Could not delete Brand Lift Survey',
          text: error.message,
          type: 'danger',
          timeout: true,
          delay: 6000,
        });
        throw error;
      }
    },

    async loadShared({ commit }, sharedToken) {
      commit('setLoading', true);
      try {
        const body = await BLSService.getSharedReport(sharedToken);
        const campaignId = body?.campaignId;
        if (!campaignId) throw new Error(body?.message || 'Could not load Brand Lift results.');
        commit('setList', { campaignId, list: unwrapList(body) });
        const current = pickLatestSurvey(unwrapList(body));
        if (current?.id && body.report) {
          commit('setReport', { id: current.id, report: body.report });
        }
        if (current?.id && body.daily) {
          commit('setDaily', { id: current.id, report: body.daily });
        }
        if (current?.id && body.optimization) {
          commit('setOptimization', { id: current.id, log: body.optimization });
        }
        if (body.report && Number(body.report.status) >= 400) {
          throw new Error(body.report.message || 'Could not load Brand Lift results.');
        }
        return campaignId;
      } catch (error) {
        notify(commit, {
          title: 'Could not load Brand Lift results',
          text: error.message,
          type: 'danger',
          timeout: true,
          delay: 6000,
        });
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },

    async loadReport({ commit }, id) {
      const [report, daily, optimization] = await Promise.all([
        BLSService.getReport(id),
        BLSService.getDailyPpl(id).catch(() => ({ report: [], grain: 'day' })),
        BLSService.getOptimizationLog(id).catch(() => ({ current: {}, events: [] })),
      ]);
      commit('setReport', { id, report });
      commit('setDaily', { id, report: daily });
      commit('setOptimization', { id, log: optimization });
      return { report, daily, optimization };
    },

    async loadTestDataStatus(_, id) {
      return BLSService.getTestData(id);
    },

    async insertTestData({ commit, dispatch, state }, { id, payload }) {
      try {
        const result = await BLSService.insertTestData(id, payload);
        if (result?.startDate) {
          for (const list of Object.values(state.byCampaignId)) {
            const survey = (list || []).find((item) => item.id === id);
            if (survey) {
              commit('upsertSurvey', { ...survey, startDate: result.startDate });
              break;
            }
          }
        }
        notify(commit, {
          title: 'Test numbers inserted',
          text: 'Fake Brand Lift warehouse rows were written for this survey’s published live IDs. Start date was set to the start of that data.',
          type: 'success',
          timeout: true,
          delay: 5000,
        });
        dispatch('loadReport', id).catch(() => {});
        return result;
      } catch (error) {
        notify(commit, {
          title: 'Could not insert test numbers',
          text: error.message,
          type: 'danger',
          timeout: true,
          delay: 7000,
        });
        throw error;
      }
    },

    async wipeTestData({ commit, dispatch }, id) {
      try {
        const result = await BLSService.wipeTestData(id);
        notify(commit, {
          title: 'Test numbers removed',
          text: 'Fake Brand Lift warehouse rows for this survey were deleted.',
          type: 'success',
          timeout: true,
          delay: 4000,
        });
        dispatch('loadReport', id).catch(() => {});
        return result;
      } catch (error) {
        notify(commit, {
          title: 'Could not remove test numbers',
          text: error.message,
          type: 'danger',
          timeout: true,
          delay: 7000,
        });
        throw error;
      }
    },
  },
};
